// Settings Module
import { api } from '../core/api-client.js';
import { showNotification } from '../utils/notifications.js';
import { hasPermission } from '../utils/permissions.js';
import { applySystemBranding } from '../utils/branding.js';

// Permission definitions for the matrix
const ALL_PERMISSIONS = [
    { key: 'view:dashboard', label: 'View Dashboard' },
    { key: 'view:agents', label: 'View Agents' },
    { key: 'manage:agents', label: 'Manage Agents' },
    { key: 'view:threats', label: 'View Threats' },
    { key: 'manage:threats', label: 'Manage Threats' },
    { key: 'view:billing', label: 'View Billing' },
    { key: 'manage:billing', label: 'Manage Billing' },
    { key: 'manage:users', label: 'Manage Users' },
    { key: 'view:settings', label: 'View Settings' },
    { key: 'manage:settings', label: 'Manage Settings' }
];

// Role permissions mapping (matches backend auth.go)
const ROLE_PERMISSIONS = {
    admin: [
        'view:dashboard', 'view:agents', 'manage:agents',
        'view:threats', 'manage:threats', 'view:billing',
        'manage:billing', 'manage:users', 'view:settings', 'manage:settings'
    ],
    operator: [
        'view:dashboard', 'view:agents', 'manage:agents',
        'view:threats', 'manage:threats'
    ],
    viewer: [
        'view:dashboard', 'view:agents', 'view:threats'
    ]
};

export async function loadSettings() {
    if (!hasPermission('view:settings')) {
        console.log('[Settings] No permission to view settings');
        return;
    }

    try {
        // Load all data in parallel
        const [settings, rawUsers, roles, passwordPolicy] = await Promise.all([
            api.getSettings().catch(e => { console.warn('[Settings] Failed to load settings:', e); return {}; }),
            api.getUsers().catch(e => { console.warn('[Settings] Failed to load users:', e); return []; }),
            api.getRoles().catch(e => { console.warn('[Settings] Failed to load roles:', e); return []; }),
            api.getPasswordPolicy().catch(e => { console.warn('[Settings] Failed to load password policy:', e); return null; })
        ]);

        const users = Array.isArray(rawUsers) ? rawUsers : [];

        console.log('[Settings] Loaded all data:', { settings, users, roles, passwordPolicy });

        // Update each section
        updateSettingsUI(settings);
        updateUsersSection(users);
        updateRolesSection(roles, users);
        updatePasswordPolicySection(passwordPolicy, users);

    } catch (error) {
        console.error('[Settings] Failed to load:', error);
        showNotification('Failed to load settings', 'error');
    }
}

function updateSettingsUI(settings) {
    try {
        console.log('[Settings] Updating UI with settings:', settings);
        let updatedCount = 0;

        Object.keys(settings).forEach(key => {
            const input = document.querySelector(`[name="${key}"]`);
            if (input) {
                const value = settings[key];

                if (input.type === 'checkbox') {
                    input.checked = value === true || value === 'true' || value === 1;
                } else if (input.tagName === 'SELECT') {
                    input.value = String(value);
                } else {
                    input.value = value !== null && value !== undefined ? String(value) : '';
                }
                updatedCount++;
            }
        });

        console.log(`[Settings] Updated ${updatedCount} fields`);
        if (settings.system_name != null && settings.system_name !== undefined) {
            applySystemBranding(String(settings.system_name));
        }
    } catch (error) {
        console.error('[Settings] Error updating UI:', error);
    }
}

function updateUsersSection(users) {
    const tbody = document.getElementById('users-table-body');
    const adminCount = document.getElementById('admin-count');
    const operatorCount = document.getElementById('operator-count');
    const viewerCount = document.getElementById('viewer-count');
    const totalCount = document.getElementById('total-user-count');

    if (!tbody) return;

    // Count by role
    const admins = users.filter(u => u.role === 'admin');
    const operators = users.filter(u => u.role === 'operator');
    const viewers = users.filter(u => u.role === 'viewer');

    if (adminCount) adminCount.textContent = admins.length;
    if (operatorCount) operatorCount.textContent = operators.length;
    if (viewerCount) viewerCount.textContent = viewers.length;
    if (totalCount) totalCount.textContent = users.length;

    if (!users || users.length === 0) {
        tbody.innerHTML = '<tr><td colspan="6" class="px-4 py-6 text-center text-slate-500">No users found</td></tr>';
        return;
    }

    const attrStr = (v) => JSON.stringify(v == null ? '' : String(v)).replace(/"/g, '&quot;');

    tbody.innerHTML = users.map(user => {
        const roleColor = user.role === 'admin' ? 'neonRed' : 'neonCyan';
        const createdDate = user.created_at ? new Date(user.created_at).toLocaleDateString() : '-';

        return `
            <tr class="border-b border-slate-800 hover:bg-slate-800/50">
                <td class="px-4 py-3">
                    <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-white font-bold text-xs">
                            ${user.username.charAt(0).toUpperCase()}
                        </div>
                        <span class="text-white font-medium">${user.username}</span>
                    </div>
                </td>
                <td class="px-4 py-3">
                    <span class="px-2 py-1 bg-${roleColor}/10 text-${roleColor} text-[10px] font-bold uppercase rounded">
                        ${user.role}
                    </span>
                </td>
                <td class="px-4 py-3 text-white text-sm">${user.name || '-'}</td>
                <td class="px-4 py-3 text-slate-400 text-sm">${user.email || '-'}</td>
                <td class="px-4 py-3 text-slate-400 text-sm">${createdDate}</td>
                <td class="px-4 py-3 text-right">
                    <button type="button" data-testid="users-row-edit" onclick="editUser(${user.id}, ${attrStr(user.username)}, ${attrStr(user.role)}, ${attrStr(user.name || '')}, ${attrStr(user.email || '')})"
                        class="px-2 py-1 text-neonCyan hover:bg-neonCyan/10 rounded text-xs transition-all mr-2"
                        title="Edit">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button type="button" data-testid="users-row-delete" onclick="deleteUser(${user.id})"
                        class="px-2 py-1 text-neonRed hover:bg-neonRed/10 rounded text-xs transition-all"
                        title="Delete">
                        <i class="fas fa-trash"></i>
                    </button>
                </td>
            </tr>
        `;
    }).join('');
}

function updateRolesSection(roles, users) {
    // Note: ID in HTML is 'roles-container', previously mismatch caused empty page
    const roleCards = document.getElementById('roles-container');
    const permissionBody = document.getElementById('permission-matrix-body');

    if (!roleCards) {
        // Silent return as roles might not be on the current page
        return;
    }

    // Default empty array if users is invalid
    const safeUsers = Array.isArray(users) ? users : [];

    // Count users per role
    const userCounts = {
        admin: safeUsers.filter(u => u.role === 'admin').length,
        operator: safeUsers.filter(u => u.role === 'operator').length,
        viewer: safeUsers.filter(u => u.role === 'viewer').length
    };

    // Render role cards
    const roleConfigs = [
        { name: 'Admin', key: 'admin', color: 'neonRed', badge: 'Full Access', desc: 'Complete system access and control' },
        { name: 'Operator', key: 'operator', color: 'neonCyan', badge: 'Limited', desc: 'Monitor and manage agents' },
        { name: 'Viewer', key: 'viewer', color: 'neonGreen', badge: 'Read Only', desc: 'View-only access to dashboard' }
    ];

    const safeRoles = Array.isArray(roles) ? roles : [];
    const customRoles = safeRoles.filter(r => !r.is_system);

    const systemCards = roleConfigs.map(role => {
        const permissions = ROLE_PERMISSIONS[role.key] || [];
        const userCount = userCounts[role.key] || 0;

        return `
            <div class="cyber-panel p-6 rounded border-l-4 border-${role.color}">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-black text-white uppercase">${role.name}</h3>
                    <span class="px-2 py-1 bg-${role.color}/10 text-${role.color} text-[10px] font-bold uppercase rounded">
                        ${role.badge}
                    </span>
                </div>
                <p class="text-slate-400 text-sm mb-4">${role.desc}</p>
                <div class="space-y-2">
                    ${ALL_PERMISSIONS.slice(0, 5).map(perm => {
            const hasIt = permissions.includes(perm.key);
            return `
                            <div class="flex items-center gap-2 text-xs text-slate-500">
                                <i class="fas ${hasIt ? 'fa-check text-neonGreen' : 'fa-times text-neonRed'}"></i>
                                ${perm.label}
                            </div>
                        `;
        }).join('')}
                </div>
                <div class="mt-4 pt-4 border-t border-slate-800 text-xs text-slate-600">
                    <span class="font-bold">${userCount}</span> users assigned
                </div>
            </div>
        `;
    }).join('');

    const customCards = customRoles.map(role => {
        const perms = role.permissions_list || [];
        return `
            <div class="cyber-panel p-6 rounded border-l-4 border-neonCyan">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-black text-white uppercase">${role.name}</h3>
                    <span class="px-2 py-1 bg-neonCyan/10 text-neonCyan text-[10px] font-bold uppercase rounded">Custom</span>
                </div>
                <p class="text-slate-400 text-sm mb-4">${role.description || 'No description'}</p>
                <div class="text-xs text-slate-500 mb-4">
                    <span class="px-2 py-1 bg-slate-800 rounded">${perms.length} permissions</span>
                </div>
                <div class="flex gap-2 pt-4 border-t border-slate-800">
                    <button type="button" onclick="editRole(${role.id})"
                        class="flex-1 text-xs bg-slate-800 text-neonCyan py-2 rounded hover:bg-slate-700 font-bold uppercase">
                        <i class="fas fa-edit mr-1"></i> Edit
                    </button>
                    <button type="button" onclick="deleteRole(${role.id})"
                        class="flex-1 text-xs bg-slate-800 text-red-400 py-2 rounded hover:bg-slate-700 font-bold uppercase">
                        <i class="fas fa-trash mr-1"></i> Delete
                    </button>
                </div>
            </div>
        `;
    }).join('');

    roleCards.innerHTML = systemCards + customCards + `
        <div class="cyber-panel p-6 rounded border-l-4 border-slate-600 flex items-center justify-center">
            <button type="button" onclick="openCreateRoleModal()" class="text-slate-500 hover:text-neonCyan transition-all">
                <i class="fas fa-plus-circle text-3xl mb-2"></i>
                <div class="text-xs font-bold uppercase">Create Custom Role</div>
            </button>
        </div>
    `;

    // Render permission matrix
    if (permissionBody) {
        permissionBody.innerHTML = ALL_PERMISSIONS.map(perm => {
            const adminHas = ROLE_PERMISSIONS.admin.includes(perm.key);
            const operatorHas = ROLE_PERMISSIONS.operator.includes(perm.key);
            const viewerHas = ROLE_PERMISSIONS.viewer.includes(perm.key);

            return `
                <tr class="border-b border-slate-800 hover:bg-slate-800/50">
                    <td class="px-4 py-3 text-white">${perm.label}</td>
                    <td class="px-4 py-3 text-center">
                        <i class="fas ${adminHas ? 'fa-check text-neonGreen' : 'fa-times text-neonRed'}"></i>
                    </td>
                    <td class="px-4 py-3 text-center">
                        <i class="fas ${operatorHas ? 'fa-check text-neonGreen' : 'fa-times text-neonRed'}"></i>
                    </td>
                    <td class="px-4 py-3 text-center">
                        <i class="fas ${viewerHas ? 'fa-check text-neonGreen' : 'fa-times text-neonRed'}"></i>
                    </td>
                </tr>
            `;
        }).join('');
    }
}

function updatePasswordPolicySection(policy, users) {
    if (!policy) return;

    // Update Status Cards (Real Data)
    const lastUpdatedEl = document.getElementById('policy-last-updated');
    const userCountEl = document.getElementById('policy-user-count');
    const strengthEl = document.getElementById('policy-strength-text');

    if (lastUpdatedEl && policy.updated_at) {
        lastUpdatedEl.textContent = new Date(policy.updated_at).toLocaleDateString();
    }

    if (userCountEl) {
        // Use safe users array length
        const count = Array.isArray(users) ? users.length : 0;
        userCountEl.textContent = count;
    }

    if (strengthEl) {
        const lengthScore = policy.min_length >= 12 ? 2 : (policy.min_length >= 8 ? 1 : 0);
        const complexityScore = [
            policy.require_uppercase,
            policy.require_lowercase,
            policy.require_numbers,
            policy.require_special_chars
        ].filter(Boolean).length;

        // Check password history for bonus point
        const historyScore = policy.password_history > 0 ? 1 : 0;

        let strength = "WEAK";
        let color = "text-neonRed";

        const totalScore = lengthScore + complexityScore + historyScore; // Max ~7

        if (totalScore >= 5) {
            strength = "STRONG";
            color = "text-neonGreen";
        } else if (totalScore >= 3) {
            strength = "MODERATE";
            color = "text-neonAmber";
        }

        strengthEl.textContent = strength;
        strengthEl.className = `text-3xl font-black ${color} italic`;
        strengthEl.parentElement.classList.remove('border-neonAmber'); // Remove static border
        strengthEl.parentElement.classList.add(`border-${color.replace('text-', '')}`);

        // Update Policy Status Text dynamically based on being active
        const statusTextEl = document.getElementById('policy-status-text');
        if (statusTextEl) {
            statusTextEl.textContent = "ACTIVE";
            statusTextEl.className = "text-2xl font-black text-neonGreen italic";
        }
    }

    // Map API fields to form field IDs
    const fieldMappings = {
        min_length: 'policy_min_length',
        expiration_days: 'policy_expiration_days',
        require_uppercase: 'policy_require_uppercase',
        require_lowercase: 'policy_require_lowercase',
        require_numbers: 'policy_require_numbers',
        require_special_chars: 'policy_require_special_chars',
        password_history: 'policy_password_history',
        max_login_attempts: 'policy_max_login_attempts',
        lockout_duration: 'policy_lockout_duration'
    };

    Object.entries(fieldMappings).forEach(([apiKey, fieldId]) => {
        const element = document.getElementById(fieldId);
        if (element && policy[apiKey] !== undefined) {
            if (element.type === 'checkbox') {
                if (apiKey === 'password_history') {
                    // Special case: int to boolean
                    element.checked = policy[apiKey] > 0;
                } else {
                    element.checked = policy[apiKey] === true;
                }
            } else {
                element.value = policy[apiKey];
            }
        }
    });

    console.log('[Settings] Password policy loaded:', policy);
}

export async function handleSaveSettings(e) {
    e.preventDefault();

    try {
        const form = e.target;
        const settings = {};

        const saveBtn = form.querySelector('button[type="submit"]');
        let originalBtnText = "";
        if (saveBtn) {
            originalBtnText = saveBtn.innerHTML;
            saveBtn.disabled = true;
            saveBtn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Saving...';
        }

        // Collect only general/notification settings, skip password policy fields
        Array.from(form.elements).forEach(element => {
            if (!element.name || element.disabled) return;
            if (element.type === 'submit' || element.type === 'button') return;
            // Skip password policy fields (they have their own save button)
            if (element.name.startsWith('policy_')) return;

            if (element.type === 'checkbox') {
                settings[element.name] = element.checked;
            } else if (element.type === 'radio') {
                if (element.checked) {
                    settings[element.name] = element.value;
                }
            } else if (element.tagName === 'SELECT') {
                settings[element.name] = element.value;
            } else {
                settings[element.name] = element.value;
            }
        });

        console.log('[Settings] Saving payload:', settings);

        await api.updateSettings(settings);

        if (settings.system_name) {
            applySystemBranding(settings.system_name);
        }

        showNotification('Settings saved successfully!', 'success');

        if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.innerHTML = originalBtnText;
        }
    } catch (error) {
        console.error('[Settings] Failed to save:', error);
        showNotification('Failed to save settings: ' + (error.message || 'Unknown error'), 'error');

        const saveBtn = e.target.querySelector('button[type="submit"]');
        if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.innerHTML = '<i class="fas fa-save mr-2"></i>Save Settings';
        }
    }
}

async function savePasswordPolicy() {
    try {
        const policy = {
            min_length: parseInt(document.getElementById('policy_min_length')?.value) || 8,
            expiration_days: parseInt(document.getElementById('policy_expiration_days')?.value) || 90,
            require_uppercase: document.getElementById('policy_require_uppercase')?.checked || false,
            require_lowercase: document.getElementById('policy_require_lowercase')?.checked || false,
            require_numbers: document.getElementById('policy_require_numbers')?.checked || false,
            require_special_chars: document.getElementById('policy_require_special_chars')?.checked || false,
            // Access checkbox state properly (5 if checked, 0 if unchecked)
            password_history: document.getElementById('policy_password_history')?.checked ? 5 : 0,
            max_login_attempts: parseInt(document.getElementById('policy_max_login_attempts')?.value) || 5,
            lockout_duration: parseInt(document.getElementById('policy_lockout_duration')?.value) || 30
        };

        console.log('[Settings] Saving password policy:', policy);

        await api.updatePasswordPolicy(policy);
        showNotification('Password policy saved successfully!', 'success');

    } catch (error) {
        console.error('[Settings] Failed to save password policy:', error);
        showNotification('Failed to save password policy: ' + (error.message || 'Unknown error'), 'error');
    }
}

async function deleteUser(userId) {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
        await api.deleteUser(userId);
        showNotification('User deleted successfully', 'success');
        // Reload users
        const users = await api.getUsers();
        const roles = await api.getRoles().catch(() => []);
        updateUsersSection(users);
        updateRolesSection(roles, users);
    } catch (error) {
        console.error('[Settings] Failed to delete user:', error);
        showNotification('Failed to delete user: ' + (error.message || 'Unknown error'), 'error');
    }
}


function populateUserRoleSelect(selectedRole = 'operator') {
    const roleInput = document.getElementById('user-modal-role');
    if (!roleInput) return;

    const builtins = [
        { value: 'admin', label: 'Admin' },
        { value: 'operator', label: 'Operator' },
        { value: 'viewer', label: 'Viewer' }
    ];
    const builtinValues = new Set(builtins.map(b => b.value));

    // Keep prior custom options aside until we refresh from API asynchronously
    const currentCustom = Array.from(roleInput.querySelectorAll('option'))
        .map(o => o.value)
        .filter(v => v && !builtinValues.has(v));

    roleInput.innerHTML = builtins.map(b =>
        `<option value="${b.value}">${b.label}</option>`
    ).join('');

    // Preserve currently selected custom role option until async load completes
    if (selectedRole && !builtinValues.has(selectedRole)) {
        const opt = document.createElement('option');
        opt.value = selectedRole;
        opt.textContent = selectedRole;
        roleInput.appendChild(opt);
    } else {
        currentCustom.forEach(v => {
            if (v === selectedRole) return;
            const opt = document.createElement('option');
            opt.value = v;
            opt.textContent = v;
            roleInput.appendChild(opt);
        });
    }

    roleInput.value = selectedRole || 'operator';

    api.getRoles().then(roles => {
        const custom = (Array.isArray(roles) ? roles : []).filter(r => !r.is_system);
        const keep = roleInput.value;
        roleInput.innerHTML = builtins.map(b =>
            `<option value="${b.value}">${b.label}</option>`
        ).join('') + custom.map(r =>
            `<option value="${escapeHtmlAttr(r.name)}">${escapeHtmlAttr(r.name)} (custom)</option>`
        ).join('');
        if (keep && Array.from(roleInput.options).some(o => o.value === keep)) {
            roleInput.value = keep;
        } else {
            roleInput.value = 'operator';
        }
    }).catch(() => { /* keep builtins */ });
}

function escapeHtmlAttr(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function openAddUserModal() {
    const modal = document.getElementById('user-modal');
    const form = document.getElementById('user-modal-form');
    const title = document.getElementById('user-modal-title');
    const usernameInput = document.getElementById('user-modal-username');
    const modeInput = document.getElementById('user-modal-mode');
    const passwordHelp = document.getElementById('password-help-text');
    const nameInput = document.getElementById('user-modal-name');
    const emailInput = document.getElementById('user-modal-email');

    if (!modal) return;

    // Reset form
    form.reset();
    document.getElementById('user-modal-id').value = '';
    if (nameInput) nameInput.value = '';
    if (emailInput) emailInput.value = '';

    // Set Create Mode
    modeInput.value = 'create';
    title.textContent = 'Add Operator';

    // Enable username
    usernameInput.disabled = false;

    // Reset password help
    passwordHelp.textContent = 'Required for new users';

    populateUserRoleSelect('operator');
    modal.classList.remove('hidden');
}

function closeUserModal() {
    const modal = document.getElementById('user-modal');
    if (modal) modal.classList.add('hidden');
}

async function handleUserModalSubmit(event) {
    event.preventDefault();

    const mode = document.getElementById('user-modal-mode').value;
    const id = document.getElementById('user-modal-id').value;
    const username = document.getElementById('user-modal-username').value;
    const role = document.getElementById('user-modal-role').value;
    const password = document.getElementById('user-modal-password').value;
    const name = document.getElementById('user-modal-name').value;
    const email = document.getElementById('user-modal-email').value;

    try {
        if (mode === 'create') {
            if (!password) {
                showNotification('Password is required for new users', 'error');
                return;
            }
            if (password.length < 8) {
                showNotification('Password must be at least 8 characters', 'error');
                return;
            }
            await api.createUser({ username, role, password, name, email });
            showNotification('User created successfully!', 'success');
        } else {
            // Edit Mode
            const updateData = { role, name, email };
            if (password && password.trim().length > 0) {
                if (password.length < 8) {
                    showNotification('Password must be at least 8 characters', 'error');
                    return;
                }
                updateData.password = password;
            }
            await api.updateUser(id, updateData);
            showNotification('User updated successfully!', 'success');
        }

        closeUserModal();
        // Reload users
        const users = await api.getUsers();
        // Roles might not need refresh but good to keep sync
        const roles = await api.getRoles().catch(() => []);
        updateUsersSection(users);
        if (typeof updateRolesSection === 'function') {
            updateRolesSection(roles, users);
        }

    } catch (error) {
        console.error(`[Settings] Failed to ${mode} user:`, error);
        showNotification(`Failed to ${mode} user: ` + (error.message || 'Unknown error'), 'error');
    }
}

function createUser(userData) {
    // Legacy support or direct use
}

function editUser(id, currentUsername, currentRole, currentName, currentEmail) {
    const modal = document.getElementById('user-modal');
    const form = document.getElementById('user-modal-form');
    const title = document.getElementById('user-modal-title');
    const usernameInput = document.getElementById('user-modal-username');
    const roleInput = document.getElementById('user-modal-role');
    const modeInput = document.getElementById('user-modal-mode');
    const idInput = document.getElementById('user-modal-id');
    const passwordHelp = document.getElementById('password-help-text');
    const nameInput = document.getElementById('user-modal-name');
    const emailInput = document.getElementById('user-modal-email');

    if (!modal) return;

    // Set Edit Mode
    modeInput.value = 'edit';
    idInput.value = id;
    title.textContent = 'Edit Operator';

    // Populate Data
    usernameInput.value = currentUsername;
    usernameInput.disabled = true; // Cannot change username
    populateUserRoleSelect(currentRole || 'operator');
    if (nameInput) nameInput.value = currentName || '';
    if (emailInput) emailInput.value = currentEmail || '';

    // Reset password field (empty means no change)
    document.getElementById('user-modal-password').value = '';
    passwordHelp.textContent = 'Leave blank to keep current password';

    modal.classList.remove('hidden');
}

// Expose functions to global scope for HTML event handlers
window.savePasswordPolicy = savePasswordPolicy;
window.deleteUser = deleteUser;
window.editUser = editUser;
window.openAddUserModal = openAddUserModal;
window.closeUserModal = closeUserModal;
window.handleUserModalSubmit = handleUserModalSubmit;

function fillRolePermissionCheckboxes(selected = []) {
    const container = document.getElementById('role-modal-permissions');
    if (!container) return;
    const selectedSet = new Set(selected);
    container.innerHTML = ALL_PERMISSIONS.map(perm => `
        <label class="flex items-center gap-2 text-sm text-slate-300 cursor-pointer hover:text-white">
            <input type="checkbox" name="role-perm" value="${perm.key}"
                class="rounded border-slate-600 bg-slate-800 text-neonCyan focus:ring-neonCyan"
                ${selectedSet.has(perm.key) ? 'checked' : ''}>
            ${perm.label}
        </label>
    `).join('');
}

function openCreateRoleModal() {
    const modal = document.getElementById('roleModal');
    if (!modal) {
        showNotification('Role modal not available on this page', 'error');
        return;
    }
    document.getElementById('role-modal-title').innerHTML =
        '<i class="fas fa-user-shield text-neonCyan"></i> Create Role';
    document.getElementById('role-modal-id').value = '';
    document.getElementById('role-modal-name').value = '';
    document.getElementById('role-modal-name').disabled = false;
    document.getElementById('role-modal-description').value = '';
    fillRolePermissionCheckboxes([]);
    modal.classList.remove('hidden');
}

function closeRoleModal() {
    const modal = document.getElementById('roleModal');
    if (modal) modal.classList.add('hidden');
}

async function editRole(id) {
    try {
        const roles = await api.getRoles();
        const role = (Array.isArray(roles) ? roles : []).find(r => String(r.id) === String(id));
        if (!role) {
            showNotification('Role not found', 'error');
            return;
        }
        if (role.is_system) {
            showNotification('System roles cannot be edited', 'error');
            return;
        }
        const modal = document.getElementById('roleModal');
        if (!modal) {
            showNotification('Role modal not available on this page', 'error');
            return;
        }
        document.getElementById('role-modal-title').innerHTML =
            '<i class="fas fa-user-shield text-neonCyan"></i> Edit Role';
        document.getElementById('role-modal-id').value = role.id;
        document.getElementById('role-modal-name').value = role.name || '';
        document.getElementById('role-modal-description').value = role.description || '';
        fillRolePermissionCheckboxes(role.permissions_list || []);
        modal.classList.remove('hidden');
    } catch (error) {
        console.error('[Settings] Failed to load role:', error);
        showNotification('Failed to load role: ' + (error.message || 'Unknown error'), 'error');
    }
}

async function handleRoleModalSubmit(event) {
    event.preventDefault();
    const id = document.getElementById('role-modal-id').value;
    const name = document.getElementById('role-modal-name').value.trim();
    const description = document.getElementById('role-modal-description').value.trim();
    const permissions = Array.from(document.querySelectorAll('#role-modal-permissions input[name="role-perm"]:checked'))
        .map(el => el.value);

    if (!name) {
        showNotification('Role name is required', 'error');
        return;
    }

    const payload = { name, description, permissions };
    try {
        if (id) {
            await api.updateRole(id, payload);
            showNotification('Role updated successfully', 'success');
        } else {
            await api.createRole(payload);
            showNotification('Role created successfully', 'success');
        }
        closeRoleModal();
        await loadSettings();
        if (typeof window.loadRoles === 'function') {
            window.loadRoles();
        }
    } catch (error) {
        console.error('[Settings] Role save failed:', error);
        showNotification('Failed to save role: ' + (error.message || 'Unknown error'), 'error');
    }
}

async function deleteCustomRole(id) {
    if (!confirm('Are you sure you want to delete this role?')) return;
    try {
        await api.deleteRole(id);
        showNotification('Role deleted successfully', 'success');
        await loadSettings();
        if (typeof window.loadRoles === 'function') {
            window.loadRoles();
        }
    } catch (error) {
        console.error('[Settings] Role delete failed:', error);
        showNotification('Failed to delete role: ' + (error.message || 'Unknown error'), 'error');
    }
}

// Prefer this when settings module initializes after roles.js
window.deleteRole = deleteCustomRole;

// Test Functions
async function testEmailConnection() {
    console.log('[Settings] Testing Email Connection...');
    const btn = event.target.closest('button');
    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Testing...';

    const email_smtp_host = document.querySelector('[name="email_smtp_host"]').value;
    const email_smtp_port = document.querySelector('[name="email_smtp_port"]').value;
    const email_smtp_username = document.querySelector('[name="email_smtp_username"]').value;
    const email_smtp_password = document.querySelector('[name="email_smtp_password"]').value;

    try {
        await api.post('/settings/test-email', {
            email_smtp_host,
            email_smtp_port,
            email_smtp_username,
            email_smtp_password
        });
        showNotification('Email connection test successful', 'success');
    } catch (error) {
        console.error('[Settings] Email test failed:', error);
        showNotification('Email test failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
        btn.disabled = false;
        btn.innerHTML = originalText;
    }
}

async function testSlackDetails() {
    console.log('[Settings] Testing Slack Webhook...');
    const btn = event.target.closest('button');
    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Sending...';

    const urlEl = document.querySelector('[name="slack_webhook_url"]');
    const slack_webhook_url = (urlEl && urlEl.value && urlEl.value.trim()) || '';

    try {
        await api.post('/settings/test-slack', { slack_webhook_url });
        showNotification('Test message sent to Slack', 'success');
    } catch (error) {
        console.error('[Settings] Slack test failed:', error);
        showNotification('Slack test failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
        btn.disabled = false;
        btn.innerHTML = originalText;
    }
}

async function testDiscordMessage() {
    console.log('[Settings] Testing Discord Webhook...');
    const btn = event.target.closest('button');
    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Sending...';

    const urlEl = document.querySelector('[name="discord_webhook_url"]');
    const discord_webhook_url = (urlEl && urlEl.value && urlEl.value.trim()) || '';

    try {
        await api.post('/settings/test-discord', { discord_webhook_url });
        showNotification('Test message sent to Discord', 'success');
    } catch (error) {
        console.error('[Settings] Discord test failed:', error);
        showNotification('Discord test failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
        btn.disabled = false;
        btn.innerHTML = originalText;
    }
}

async function testTelegramMessage(ev) {
    console.log('[Settings] Testing Telegram Notification...');
    const btn = ev && ev.target && ev.target.closest('button');
    const originalText = btn ? btn.innerHTML : '';
    if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Sending...';
    }

    const tokenEl = document.querySelector('[name="telegram_bot_token"]');
    const chatEl = document.querySelector('[name="telegram_chat_id"]');
    const telegram_bot_token = (tokenEl && tokenEl.value && tokenEl.value.trim()) || '';
    const telegram_chat_id = (chatEl && chatEl.value && chatEl.value.trim()) || '';

    try {
        await api.post('/settings/test-telegram', { telegram_bot_token, telegram_chat_id });
        showNotification('Test message sent to Telegram', 'success');
    } catch (error) {
        console.error('[Settings] Telegram test failed:', error);
        showNotification('Telegram test failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
        if (btn) {
            btn.disabled = false;
            btn.innerHTML = originalText;
        }
    }
}

// Make functions globally available for HTML onclick
window.handleSaveSettings = handleSaveSettings;
window.testEmailConnection = testEmailConnection;
window.testSlackDetails = testSlackDetails;
window.testDiscordMessage = testDiscordMessage;
window.testTelegramMessage = testTelegramMessage;
window.savePasswordPolicy = savePasswordPolicy;
window.deleteUser = deleteUser;
window.openAddUserModal = openAddUserModal;
window.openCreateRoleModal = openCreateRoleModal;
window.closeRoleModal = closeRoleModal;
window.editRole = editRole;
window.handleRoleModalSubmit = handleRoleModalSubmit;
// deleteRole is owned by roles.js (also refreshes settings cards)
